import type { OverallStatus } from '@upl/evidence-schema';

export interface StatusLanguage {
  eyebrow: string;
  title: string;
  explanation: string;
  tone: 'positive' | 'warning' | 'neutral';
}

export const STATUS_LANGUAGE: Readonly<Record<OverallStatus, StatusLanguage>> = {
  VERIFIED_PROVENANCE: {
    eyebrow: 'Verified provenance',
    title: 'Verifiable provenance information is attached to this file.',
    explanation: 'The credential and its binding to this file validated under the stated policy. This does not establish the factual truth of the content.',
    tone: 'positive',
  },
  PROVENANCE_PRESENT: {
    eyebrow: 'Provenance present',
    title: 'Provenance information is present, but not all trust or validation conditions are established.',
    explanation: 'Inspect integrity, trust, signer, and history independently to see which conditions are known and which remain unresolved.',
    tone: 'neutral',
  },
  INVALID_OR_TAMPERED: {
    eyebrow: 'Validation failed',
    title: 'The available provenance information failed one or more validation checks.',
    explanation: 'The report identifies the affected integrity or trust conditions without making a claim about the content itself.',
    tone: 'warning',
  },
  NO_SUPPORTED_PROVENANCE: {
    eyebrow: 'No supported provenance',
    title: 'No supported embedded provenance was found in this file.',
    explanation: 'This absence does not establish authorship, origin, or how the file was created.',
    tone: 'neutral',
  },
  INCONCLUSIVE: {
    eyebrow: 'Inconclusive',
    title: 'ORIGNTD could not reach a reliable provenance conclusion.',
    explanation: 'A processing or validation boundary prevented a supported result. Review the warnings and technical evidence.',
    tone: 'neutral',
  },
  UNSUPPORTED: {
    eyebrow: 'Unsupported file',
    title: 'This file cannot currently be evaluated by ORIGNTD.',
    explanation: 'The current release evaluates JPEG and PNG images only.',
    tone: 'neutral',
  },
};

export const UNIVERSAL_LIMITATION =
  'Provenance evidence does not establish factual truth.';

export const PRODUCT_TAGLINE = 'We show evidence. You make the judgment.';

export const DIAGNOSTIC_LANGUAGE: Readonly<Record<string, string>> = {
  NOT_FACTUAL_TRUTH: UNIVERSAL_LIMITATION,
  R1_SIGNAL_SCOPE: 'This release checks C2PA, the official C2PA trust infrastructure on the server, and a limited EXIF, XMP, and IPTC subset in JPEG and PNG files.',
  ABSENCE_NOT_AUTHORSHIP: 'Absence of supported provenance does not establish authorship or creation method.',
  EDITABLE_METADATA: 'Embedded metadata is editable and is not cryptographically verified.',
  C2PA_PARSER_FAILURE: 'The C2PA parser could not complete inspection of this file.',
  METADATA_PARSER_FAILURE: 'The supported metadata subset could not be read safely.',
  TRUST_SOURCE_UNAVAILABLE: 'The official trust source was unavailable and no usable cached snapshot existed. Trust remains unknown.',
  TRUST_EVALUATION_FAILED: 'The official trust snapshot loaded, but trust evaluation could not complete. Trust remains unknown.',
  TRUST_DATA_STALE: 'A previously valid trust snapshot was used and is explicitly marked stale.',
  ADAPTER_OUTPUT_INVALID: 'One evidence source returned data ORIGNTD could not safely interpret.',
};

export function diagnosticLanguage(code: string): string {
  return DIAGNOSTIC_LANGUAGE[code] ?? 'A technical condition limited this verification.';
}
